import { useState, useEffect } from 'react';
import { Mountain, Upload, Info } from 'lucide-react';
import { useToast } from '../../components/ui/toast';
import { submitView } from '../../lib/api';
import { SubmitPageSEO } from './SEO';
import { SuccessPopup } from '../../components/SuccessPopup';
import { useSettings } from '../../hooks/useSettings';

// Add a helper function for image processing
const processImage = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    // Create a new FileReader
    const reader = new FileReader();
    
    // Set up onloadend callback
    reader.onloadend = () => {
      // Ensure the result is a string
      if (typeof reader.result === 'string') {
        // Check if the string is valid
        if (reader.result.startsWith('data:image/')) {
          resolve(reader.result);
        } else {
          reject(new Error('Invalid image format'));
        }
      } else {
        reject(new Error('Failed to convert image to base64'));
      }
    };
    
    // Set up onerror callback
    reader.onerror = () => {
      reject(new Error('Error reading file'));
    };
    
    // Read the file as a data URL (base64)
    reader.readAsDataURL(file);
  });
};

// Add an image compression helper function
const compressImage = (file: File, maxSizeMB: number): Promise<File> => {
  return new Promise((resolve, reject) => {
    // Only compress if it's larger than maxSizeMB
    if (file.size <= maxSizeMB * 1024 * 1024) {
      resolve(file);
      return;
    }

    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target?.result as string;
      
      img.onload = () => {
        // Calculate compression ratio
        const maxSizeBytes = maxSizeMB * 1024 * 1024;
        const ratio = Math.sqrt(maxSizeBytes / file.size);
        
        // Don't go below 0.5 quality
        const quality = Math.max(0.5, Math.min(0.9, ratio));
        
        // Calculate new dimensions (maintain aspect ratio)
        let width = img.width;
        let height = img.height;
        
        // If very large, scale down dimensions
        if (width > 2000 || height > 2000) {
          const scaleFactor = 2000 / Math.max(width, height);
          width = Math.round(width * scaleFactor);
          height = Math.round(height * scaleFactor);
        }
        
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Could not get canvas context'));
          return;
        }
        
        ctx.drawImage(img, 0, 0, width, height);
        
        // Convert to blob with quality adjustment for JPEG/WebP
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error('Compression failed'));
              return;
            }
            
            const newFile = new File([blob], file.name, {
              type: file.type,
              lastModified: file.lastModified,
            });
            
            // Image compressed successfully
            resolve(newFile);
          },
          file.type,
          quality
        );
      };
      
      img.onerror = () => {
        reject(new Error('Failed to load image for compression'));
      };
    };
    
    reader.onerror = () => {
      reject(new Error('Failed to read file for compression'));
    };
  });
};

interface FormData {
  sourceName: string;
  sourceState: string;
  sourceCountry: string;
  sourceContinent: string;
  destinationName: string;
  destinationState: string;
  destinationCountry: string;
  destinationContinent: string;
  distance: string;
  distanceUnit: 'km' | 'mi';
  status: 'confirmed' | 'likely' | 'if-clear' | 'plausible' | 'dubious' | 'unlikely';
  type: string;
  description: string;
  email: string;
  submitterName: string;
  creditInfo: string;
  creditName: string;
  imageFile: File | null;
  coordinates: {
    sourceLat: string;
    sourceLong: string;
    destinationLat: string;
    destinationLong: string;
  };
}

export default function SubmitViewPage() {
  console.log('🟡 SubmitViewPage: Component mounting/rendering');
  
  const { toast } = useToast();
  const { settings, loading: settingsLoading, getMaxFileSize, getSupportedFormats, getDefaultCountry, getDistanceUnit } = useSettings();
  
  const [formData, setFormData] = useState<FormData>({
    sourceName: '',
    sourceState: '',
    sourceCountry: '',
    sourceContinent: '',
    destinationName: '',
    destinationState: '',
    destinationCountry: '',
    destinationContinent: '',
    distance: '',
    distanceUnit: 'km',
    status: 'confirmed',
    type: 'Mountain(s)',
    description: '',
    email: '',
    submitterName: '',
    creditInfo: '',
    creditName: '',
    imageFile: null,
    coordinates: {
      sourceLat: '',
      sourceLong: '',
      destinationLat: '',
      destinationLong: '',
    }
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [processedImage, setProcessedImage] = useState<string | null>(null);
  const [isProcessingImage, setIsProcessingImage] = useState(false);
  
  // Update default values when settings load (NAVIGATION FIX: removed problematic dependencies)
  useEffect(() => {
    if (settings && !settingsLoading) {
      const defaultCountry = settings?.defaultCountry || 'USA';
      const distanceUnit = settings?.distanceCalculationMethod === 'Miles' ? 'mi' : 'km';
      
      setFormData(prev => ({
        ...prev,
        sourceCountry: prev.sourceCountry || defaultCountry,
        destinationCountry: prev.destinationCountry || defaultCountry,
        distanceUnit: distanceUnit as 'km' | 'mi'
      }));
    }
  }, [settings, settingsLoading]); // Removed getDefaultCountry, getDistanceUnit dependencies

  // DEBUG: Track component lifecycle
  useEffect(() => {
    console.log('🟡 SubmitViewPage: Component mounted');
    return () => {
      console.log('🟡 SubmitViewPage: Component unmounting');
    };
  }, []);
  
  // Expose state setters for testing purposes (NAVIGATION FIX: moved to useEffect)
  // This allows Cypress to directly manipulate the state
  useEffect(() => {
    if (typeof window !== 'undefined') {
      (window as any).setPreviewUrl = setPreviewUrl;
      (window as any).setProcessedImage = setProcessedImage;
    }
    
    // Cleanup on unmount
    return () => {
      if (typeof window !== 'undefined') {
        delete (window as any).setPreviewUrl;
        delete (window as any).setProcessedImage;
      }
    };
  }, []);
  
  // Helper function to set an external image URL directly
  const setExternalImageUrl = (url: string) => {
    if (!url) return;
    
    // Validate that the URL looks like an image URL
    const isValidUrl = /^https?:\/\/.+\.(jpg|jpeg|png|gif|webp)/i.test(url) || 
                       url.includes('unsplash.com') || 
                       url.includes('imgur.com');
    
    if (!isValidUrl) {
      toast({
        title: "Invalid image URL",
        description: "The URL does not appear to be a valid image"
      });
      return;
    }
    
    // Set the preview and processed image directly
    setPreviewUrl(url);
    setProcessedImage(url);
    
    toast({
      title: "Image URL added",
      description: "External image URL has been set"
    });
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData(prev => {
        if (parent === 'coordinates') {
          return {
            ...prev,
            coordinates: {
              ...prev.coordinates,
              [child]: value
            }
          };
        }
        return prev;
      });
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  // One path for both a picked and a dropped file, so both get the same type,
  // size and processing checks.
  const handleImageFile = async (file: File) => {
    // Get dynamic settings for validation
    const maxFileSize = getMaxFileSize();
    const supportedFormats = getSupportedFormats();
    
    // Check file type using dynamic supported formats
    const fileExtension = file.name.split('.').pop()?.toLowerCase();
    const validTypes = supportedFormats.map(format => {
      switch(format.toLowerCase()) {
        case 'jpeg': return 'image/jpeg';
        case 'jpg': return 'image/jpeg';
        case 'png': return 'image/png';
        case 'gif': return 'image/gif';
        case 'webp': return 'image/webp';
        default: return `image/${format.toLowerCase()}`;
      }
    });
    
    if (!validTypes.includes(file.type)) {
      toast({
        title: "Invalid file type",
        description: `Please upload a ${supportedFormats.join(', ')} image`
      });
      return;
    }
    
    // Check file size using dynamic limit
    if (file.size > maxFileSize) {
      const maxSizeMB = Math.round(maxFileSize / (1024 * 1024));
      toast({
        title: "File too large",
        description: `Please upload an image smaller than ${maxSizeMB}MB`
      });
      return;
    }
    
    try {
      // Clear any existing image data
      setPreviewUrl(null);
      setProcessedImage(null);
      
      // Show loading state 
      setIsProcessingImage(true);
      toast({
        title: "Processing image",
        description: "Please wait while we process your image..."
      });
      
      // Compress image if larger than 5MB
      let processedFile = file;
      if (file.size > 5 * 1024 * 1024) {
        // Compressing image
        toast({
          title: "Compressing image",
          description: "Large image detected, optimizing for upload..."
        });
        processedFile = await compressImage(file, 5);
      }
      
      setFormData(prev => ({
        ...prev,
        imageFile: processedFile
      }));

      // Processing image
      // Process the image
      const processedImageData = await processImage(processedFile);
      // Image processed successfully
      
      // Update state with the processed image
      setPreviewUrl(processedImageData);
      setProcessedImage(processedImageData);
      
      // Image processed successfully
      toast({
        title: "Image added",
        description: "Image ready for submission"
      });
    } catch (error) {
      console.error('Error processing image:', error);
      toast({
        title: "Image Error",
        description: "Failed to process image. Please try another image."
      });
    } finally {
      setIsProcessingImage(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // Clear the input so choosing the same file again (after removing it, or
    // after a rejection) still fires a change event.
    e.target.value = '';
    if (file) void handleImageFile(file);
  };

  const [isDraggingImage, setIsDraggingImage] = useState(false);

  const isFileDrag = (e: React.DragEvent) => Array.from(e.dataTransfer.types).includes('Files');

  const handleImageDragOver = (e: React.DragEvent<HTMLLabelElement>) => {
    if (!isFileDrag(e)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = isProcessingImage ? 'none' : 'copy';
    if (!isDraggingImage) setIsDraggingImage(true);
  };

  const handleImageDragLeave = (e: React.DragEvent<HTMLLabelElement>) => {
    // dragleave also fires when moving over the label's own children.
    if (e.currentTarget.contains(e.relatedTarget as Node | null)) return;
    setIsDraggingImage(false);
  };

  const handleImageDrop = (e: React.DragEvent<HTMLLabelElement>) => {
    if (!isFileDrag(e)) return;
    e.preventDefault();
    setIsDraggingImage(false);
    if (isProcessingImage) return;
    const files = Array.from(e.dataTransfer.files);
    const image = files.find((f) => f.type.startsWith('image/'));
    if (!image) {
      toast({
        title: "Not an image",
        description: "Drop a photo file (JPG, PNG or WEBP) onto the upload box."
      });
      return;
    }
    if (files.length > 1) {
      toast({
        title: "One image at a time",
        description: `Using ${image.name}; the other files were ignored.`
      });
    }
    void handleImageFile(image);
  };

  // A file dropped anywhere else on the page would make the browser open it
  // and throw away everything typed into the form. Swallow those drops; only
  // the upload box accepts files.
  useEffect(() => {
    const blockStrayFileDrop = (e: DragEvent) => {
      if (e.dataTransfer && Array.from(e.dataTransfer.types).includes('Files')) {
        e.preventDefault();
      }
    };
    window.addEventListener('dragover', blockStrayFileDrop);
    window.addEventListener('drop', blockStrayFileDrop);
    return () => {
      window.removeEventListener('dragover', blockStrayFileDrop);
      window.removeEventListener('drop', blockStrayFileDrop);
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Don't allow submission if an image is still processing
    if (isProcessingImage) {
      toast({
        title: "Image still processing",
        description: "Please wait for image processing to complete before submitting"
      });
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Check if processedImage is a data URL or an external URL
      let imageValue = processedImage || '';
      
      // Image format detection for processing
      
      // Transform form data to match API expectations
      const apiFormData = {
        source: formData.sourceName || '',
        state: formData.sourceState || '',
        country: formData.sourceCountry || '',
        continent: formData.sourceContinent || 'North America',
        destination: formData.destinationName || '',
        dest_state: formData.destinationState || '',
        dest_country: formData.destinationCountry || '',
        dest_continent: formData.destinationContinent || 'North America',
        distance_km: formData.distanceUnit === 'km' ? formData.distance : (Number(formData.distance) * 1.60934).toFixed(2),
        distance_m: formData.distanceUnit === 'mi' ? formData.distance : (Number(formData.distance) * 0.621371).toFixed(2),
        status: formData.status || 'confirmed',
        type: formData.type || 'Mountain(s)',
        source_lat: formData.coordinates.sourceLat || '',
        source_long: formData.coordinates.sourceLong || '',
        dest_lat: formData.coordinates.destinationLat || '',
        dest_long: formData.coordinates.destinationLong || '',
        description: formData.description || '',
        email: formData.email || '',
        submitterEmail: formData.email || '',
        submitterName: formData.submitterName || '',
        creditInfo: formData.creditInfo || '',
        creditName: formData.creditName || '',
        formType: 'view_submission',
        resource_2: imageValue
      };
      
      // Call the API submission function
      const success = await submitView(apiFormData);
      
      if (success) {
        // Show success popup
        setShowSuccessPopup(true);
        
        // Also show toast for additional feedback
        toast({
          title: "Submission received!",
          description: "Your view has been submitted for review. We'll notify you once it's approved."
        });
        
        // Reset form
        setFormData({
          sourceName: '',
          sourceState: '',
          sourceCountry: '',
          sourceContinent: '',
          destinationName: '',
          destinationState: '',
          destinationCountry: '',
          destinationContinent: '',
          distance: '',
          distanceUnit: 'km',
          status: 'confirmed',
          type: 'Mountain(s)',
          description: '',
          email: '',
          submitterName: '',
          creditInfo: '',
          creditName: '',
          imageFile: null,
          coordinates: {
            sourceLat: '',
            sourceLong: '',
            destinationLat: '',
            destinationLong: '',
          }
        });
        setPreviewUrl(null);
        setProcessedImage(null);
      } else {
        toast({
          title: "Submission failed",
          description: "There was an error submitting your view. Please try again."
        });
      }
    } catch (error) {
      console.error('Error submitting view:', error);
      toast({
        title: "Submission failed",
        description: "There was an error submitting your view. Please try again."
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <SubmitPageSEO />
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <h1 className="text-3xl font-bold mb-2 gradient-text">
          {settings?.submitViewTitle || 'Submit a View'}
        </h1>
        <p className="text-foreground mb-8">
          {settings?.submitViewSubtitle || 'Share your spectacular line-of-sight view with the community.'}
        </p>
        
        {settingsLoading && (
          <div className="flex items-center justify-center py-4 mb-4">
            <svg className="animate-spin h-5 w-5 text-primary mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <span className="text-sm text-muted-foreground">Loading settings...</span>
          </div>
        )}
        
        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Source Location */}
          <div className="bg-card border border-border rounded-lg p-6">
            <h2 className="text-xl font-semibold mb-4 flex items-center text-primary">
              <Mountain className="mr-2 h-5 w-5" />
              Source Location
            </h2>
            <p className="text-muted-foreground mb-4 text-sm">This is the location from where the view was observed.</p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="sourceName" className="block text-sm font-medium mb-1">
                  Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="sourceName"
                  name="sourceName"
                  value={formData.sourceName}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 bg-background border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="e.g. Mount Rainier"
                />
              </div>
              
              <div>
                <label htmlFor="sourceState" className="block text-sm font-medium mb-1">
                  State/Province <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="sourceState"
                  name="sourceState"
                  value={formData.sourceState}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 bg-background border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="e.g. Washington"
                />
              </div>
              
              <div>
                <label htmlFor="sourceCountry" className="block text-sm font-medium mb-1">
                  Country <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="sourceCountry"
                  name="sourceCountry"
                  value={formData.sourceCountry}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 bg-background border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="e.g. United States"
                />
              </div>
              
              <div>
                <label htmlFor="sourceContinent" className="block text-sm font-medium mb-1">
                  Continent
                </label>
                <input
                  type="text"
                  id="sourceContinent"
                  name="sourceContinent"
                  value={formData.sourceContinent}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 bg-background border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="e.g. North America"
                />
              </div>
              
              <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="coordinates.sourceLat" className="block text-sm font-medium mb-1">
                    Latitude (Optional)
                  </label>
                  <input
                    type="text"
                    id="coordinates.sourceLat"
                    name="coordinates.sourceLat"
                    value={formData.coordinates.sourceLat}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 bg-background border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="e.g. 47.8021"
                  />
                </div>
                
                <div>
                  <label htmlFor="coordinates.sourceLong" className="block text-sm font-medium mb-1">
                    Longitude (Optional)
                  </label>
                  <input
                    type="text"
                    id="coordinates.sourceLong"
                    name="coordinates.sourceLong"
                    value={formData.coordinates.sourceLong}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 bg-background border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="e.g. -123.7068"
                  />
                </div>
              </div>
            </div>
          </div>
          
          {/* Destination Location */}
          <div className="bg-card border border-border rounded-lg p-6">
            <h2 className="text-xl font-semibold mb-4 flex items-center text-primary">
              <Mountain className="mr-2 h-5 w-5" />
              Destination Location
            </h2>
            <p className="text-muted-foreground mb-4 text-sm">This is the distant location that was visible from the source.</p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="destinationName" className="block text-sm font-medium mb-1">
                  Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="destinationName"
                  name="destinationName"
                  value={formData.destinationName}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 bg-background border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="e.g. Mount Baker"
                />
              </div>
              
              <div>
                <label htmlFor="destinationState" className="block text-sm font-medium mb-1">
                  State/Province <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="destinationState"
                  name="destinationState"
                  value={formData.destinationState}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 bg-background border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="e.g. Washington"
                />
              </div>
              
              <div>
                <label htmlFor="destinationCountry" className="block text-sm font-medium mb-1">
                  Country <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="destinationCountry"
                  name="destinationCountry"
                  value={formData.destinationCountry}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 bg-background border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="e.g. United States"
                />
              </div>
              
              <div>
                <label htmlFor="destinationContinent" className="block text-sm font-medium mb-1">
                  Continent
                </label>
                <input
                  type="text"
                  id="destinationContinent"
                  name="destinationContinent"
                  value={formData.destinationContinent}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 bg-background border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="e.g. North America"
                />
              </div>
              
              <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="coordinates.destinationLat" className="block text-sm font-medium mb-1">
                    Latitude (Optional)
                  </label>
                  <input
                    type="text"
                    id="coordinates.destinationLat"
                    name="coordinates.destinationLat"
                    value={formData.coordinates.destinationLat}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 bg-background border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="e.g. 48.7768"
                  />
                </div>
                
                <div>
                  <label htmlFor="coordinates.destinationLong" className="block text-sm font-medium mb-1">
                    Longitude (Optional)
                  </label>
                  <input
                    type="text"
                    id="coordinates.destinationLong"
                    name="coordinates.destinationLong"
                    value={formData.coordinates.destinationLong}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 bg-background border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="e.g. -121.8145"
                  />
                </div>
              </div>
            </div>
          </div>
          
          {/* View Details */}
          <div className="bg-card border border-border rounded-lg p-6">
            <h2 className="text-xl font-semibold mb-4 flex items-center text-primary">
              <Info className="mr-2 h-5 w-5" />
              View Details
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex space-x-4">
                <div className="flex-1">
                  <label htmlFor="distance" className="block text-sm font-medium mb-1">
                    Distance <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    id="distance"
                    name="distance"
                    value={formData.distance}
                    onChange={handleInputChange}
                    required
                    min="10"
                    step="0.01"
                    className="w-full px-3 py-2 bg-background border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="e.g. 120.5"
                  />
                </div>
                
                <div className="w-24">
                  <label htmlFor="distanceUnit" className="block text-sm font-medium mb-1">
                    Unit
                  </label>
                  <select
                    id="distanceUnit"
                    name="distanceUnit"
                    value={formData.distanceUnit}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 bg-background border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value="km">km</option>
                    <option value="mi">mi</option>
                  </select>
                </div>
              </div>
              
              <div>
                <label htmlFor="status" className="block text-sm font-medium mb-1">
                  Status <span className="text-red-500">*</span>
                </label>
                <select
                  id="status"
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 bg-background border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="confirmed">Confirmed</option>
                  <option value="likely">Likely</option>
                  <option value="if-clear">If Clear</option>
                  <option value="plausible">Plausible</option>
                  <option value="dubious">Dubious</option>
                  <option value="unlikely">Unlikely</option>
                </select>
                <div className="mt-1.5">
                  <a 
                    href="/faq?q=5" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="text-xs text-primary hover:underline flex items-center"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-3 w-3 mr-1" aria-hidden="true">
                      <title>Information Icon</title>
                      <circle cx="12" cy="12" r="10"/>
                      <line x1="12" y1="8" x2="12" y2="12"/>
                      <line x1="12" y1="16" x2="12.01" y2="16"/>
                    </svg>
                    What do the Status values mean?
                  </a>
                </div>
              </div>
              
              <div>
                <label htmlFor="type" className="block text-sm font-medium mb-1">
                  Type <span className="text-red-500">*</span>
                </label>
                <select
                  id="type"
                  name="type"
                  value={formData.type}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 bg-background border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="Bridge">Bridge</option>
                  <option value="Chimney">Chimney</option>
                  <option value="Coast">Coast</option>
                  <option value="Island">Island</option>
                  <option value="Mountain(s)">Mountain(s)</option>
                  <option value="Skyline">Skyline</option>
                  <option value="Water Body">Water Body</option>
                </select>
              </div>
              
              <div className="md:col-span-2">
                <label htmlFor="description" className="block text-sm font-medium mb-1">
                  Description
                </label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows={4}
                  className="w-full px-3 py-2 bg-background border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="Provide any additional details about this view..."
                />
              </div>
            </div>
          </div>
          
          {/* Image Upload */}
          <div className="bg-card border border-border rounded-lg p-6">
            <h2 className="text-xl font-semibold mb-4 flex items-center text-primary">
              <Upload className="mr-2 h-5 w-5" />
              Image Upload
            </h2>
            <p className="text-muted-foreground mb-4 text-sm">Upload an image that shows the view. This helps verify the visibility claim.</p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="imageFile" className="block text-sm font-medium mb-2">
                  Upload Image
                </label>
                <input
                  type="file"
                  id="imageFile"
                  name="imageFile"
                  onChange={handleFileChange}
                  accept="image/*"
                  className="hidden"
                />
                <label
                  htmlFor="imageFile"
                  onDragEnter={handleImageDragOver}
                  onDragOver={handleImageDragOver}
                  onDragLeave={handleImageDragLeave}
                  onDrop={handleImageDrop}
                  data-dragging={isDraggingImage || undefined}
                  className={`flex flex-col items-center justify-center w-full h-32 px-4 transition border-2 border-dashed rounded-lg cursor-pointer ${
                    isDraggingImage
                      ? 'border-primary bg-primary/10'
                      : 'bg-background border-input hover:bg-muted/20'
                  }`}
                >
                  <div className="flex flex-col items-center justify-center pt-5 pb-6 pointer-events-none">
                    <Upload className={`w-8 h-8 mb-3 text-primary transition-transform ${isDraggingImage ? 'scale-110' : ''}`} />
                    <p className="mb-2 text-sm text-foreground" aria-live="polite">
                      {isDraggingImage ? (
                        <span className="font-semibold">Drop the image to upload it</span>
                      ) : (
                        <><span className="font-semibold">Click to upload</span> or drag and drop</>
                      )}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {settings ? 
                        `${getSupportedFormats().join(', ')} (MAX. ${Math.round(getMaxFileSize() / (1024 * 1024))}MB)` :
                        'PNG, JPG or WEBP (MAX. 10MB)'
                      }
                    </p>
                  </div>
                </label>
              </div>
              
              <div>
                {previewUrl ? (
                  <div className="h-full flex flex-col">
                    <p className="text-sm font-medium mb-2">Preview</p>
                    <div className="relative flex-1 border border-input rounded-lg overflow-hidden">
                      <img
                        src={previewUrl}
                        alt="View from source location to destination"
                        className="w-full h-full object-cover"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          setPreviewUrl(null);
                          setProcessedImage(null);
                          setFormData(prev => ({ ...prev, imageFile: null }));
                        }}
                        className="absolute top-2 right-2 p-1 bg-background/80 rounded-full text-primary hover:bg-background"
                        aria-label="Remove image"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                          <line x1="18" y1="6" x2="6" y2="18" />
                          <line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="h-full flex items-center justify-center border border-input rounded-lg bg-background/50">
                    {isProcessingImage ? (
                      <div className="flex flex-col items-center justify-center text-center p-4">
                        <svg className="animate-spin mb-2 h-6 w-6 text-primary" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <p className="text-muted-foreground text-sm">Processing image...</p>
                      </div>
                    ) : (
                      <p className="text-muted-foreground text-sm">Image preview will appear here</p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
          
          {/* Contact Information */}
          <div className="bg-card border border-border rounded-lg p-6">
            <h2 className="text-xl font-semibold mb-4 text-primary">Contact Information</h2>
            <p className="text-muted-foreground mb-4 text-sm">We'll notify you when your submission is approved.</p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label htmlFor="submitterName" className="block text-sm font-medium mb-1">
                  Submitter Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="submitterName"
                  name="submitterName"
                  value={formData.submitterName}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 bg-background border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="Your name"
                />
              </div>
              
              <div>
                <label htmlFor="email" className="block text-sm font-medium mb-1">
                  Email Address <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 bg-background border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="your.email@example.com"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="creditName" className="block text-sm font-medium mb-1">
                  Credit Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="creditName"
                  name="creditName"
                  value={formData.creditName}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 bg-background border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="Name to credit for this view/photo"
                />
              </div>
              
              <div>
                <label htmlFor="creditInfo" className="block text-sm font-medium mb-1">
                  Credit Info / Link
                </label>
                <input
                  type="text"
                  id="creditInfo"
                  name="creditInfo"
                  value={formData.creditInfo}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 bg-background border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="Website, social media, or other attribution link"
                />
              </div>
            </div>
          </div>
          
          {/* Submit Button */}
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-6 py-3 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors flex items-center disabled:opacity-70"
            >
              {isSubmitting ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" aria-hidden="true">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Sending...
                </>
              ) : (
                <>
                  Submit View
                </>
              )}
            </button>
          </div>
        </form>
      </div>
      
      {/* Success Popup */}
      <SuccessPopup 
        isOpen={showSuccessPopup}
        onClose={() => setShowSuccessPopup(false)}
        title="Thank You!"
        message="Your view has been submitted successfully. Our team will review it shortly and publish it if it meets our criteria."
      />
    </>
  );
} 